import { useCallback, useState } from 'react';

/**
 * useConfidenceConfirmation — tracks which AI-extracted fields have been
 * explicitly confirmed by the user. Red-flagged fields (below the low
 * confidence threshold) must be confirmed before the form can be saved.
 *
 * Requirements: 57.16
 */

export interface ConfidenceConfirmationState {
  /** Mark a field as confirmed by the user. */
  confirmField: (fieldName: string) => void;
  /** Check whether a specific field has been confirmed. */
  isFieldConfirmed: (fieldName: string) => boolean;
  /**
   * Returns true if every field whose confidence is below `lowThreshold`
   * has been confirmed. Pass the full set of fields with their confidence
   * scores so the hook can determine which ones require confirmation.
   */
  allRedFieldsConfirmed: (
    fields: Array<{ fieldName: string; confidence: number }>,
    lowThreshold?: number,
  ) => boolean;
  /** Clear all confirmations (e.g. when the form is reset). */
  resetConfirmations: () => void;
  /** The raw set of confirmed field names (for inspection / debugging). */
  confirmedFields: ReadonlySet<string>;
}

/**
 * Pure logic: checks whether all fields below the low threshold are
 * present in the confirmed set. Exported for unit testing.
 */
export function checkAllRedFieldsConfirmed(
  confirmedFields: ReadonlySet<string>,
  fields: Array<{ fieldName: string; confidence: number }>,
  lowThreshold = 0.4,
): boolean {
  return fields
    .filter((f) => f.confidence < lowThreshold)
    .every((f) => confirmedFields.has(f.fieldName));
}

export function useConfidenceConfirmation(): ConfidenceConfirmationState {
  const [confirmedFields, setConfirmedFields] = useState<Set<string>>(new Set());

  const confirmField = useCallback((fieldName: string) => {
    setConfirmedFields((prev) => {
      const next = new Set(prev);
      next.add(fieldName);
      return next;
    });
  }, []);

  const isFieldConfirmed = useCallback(
    (fieldName: string) => confirmedFields.has(fieldName),
    [confirmedFields],
  );

  const allRedFieldsConfirmed = useCallback(
    (
      fields: Array<{ fieldName: string; confidence: number }>,
      lowThreshold = 0.4,
    ): boolean => {
      return checkAllRedFieldsConfirmed(confirmedFields, fields, lowThreshold);
    },
    [confirmedFields],
  );

  const resetConfirmations = useCallback(() => {
    setConfirmedFields(new Set());
  }, []);

  return {
    confirmField,
    isFieldConfirmed,
    allRedFieldsConfirmed,
    resetConfirmations,
    confirmedFields,
  };
}
